import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useLang } from "@/context/LangContext";

/**
 * Bilingual SEO component for Cammino Ibleo UNESCO.
 * Renders <title>, <meta description/keywords>, OpenGraph, Twitter Card,
 * canonical link and hreflang alternates for IT/EN.
 *
 * Props:
 *   titleIt, titleEn        — page-specific title (fallback: site defaults)
 *   descriptionIt, descriptionEn — page-specific description
 *   image                   — absolute URL of preview image (fallback: hero)
 *   keywordsIt, keywordsEn  — optional
 *   type                    — "website" (default) or "article"
 */

const SITE_NAME = "Cammino Ibleo UNESCO";
const DEFAULT_IMAGE = "/images/homepage-4.jpg";

const DEFAULTS = {
  it: {
    title: "Cammino Ibleo UNESCO — 285 km da Catania a Ragusa Ibla",
    description:
      "Cammino ciclo-pedonale di 285 km lungo le ex-ferrovie della Sicilia Orientale, attraverso 10 siti UNESCO. Tappe, mappa interattiva, prodotti del territorio e sagre iblee.",
    keywords:
      "Cammino Ibleo, Val di Noto, UNESCO Sicilia, ex ferrovia Siracusa Vizzini Ragusa, ciclovia, trekking Sicilia, Catania Ragusa, Pantalica, Modica, Noto",
  },
  en: {
    title: "Cammino Ibleo UNESCO — 285 km from Catania to Ragusa Ibla",
    description:
      "285 km cycling and walking trail along the disused Eastern Sicily railways, crossing 10 UNESCO World Heritage sites. Stages, interactive map, local products and food festivals.",
    keywords:
      "Cammino Ibleo, Val di Noto, UNESCO Sicily, disused Syracuse Vizzini Ragusa railway, cycling route, hiking Sicily, Catania Ragusa, Pantalica, Modica, Noto",
  },
};

const SEO = ({
  titleIt,
  titleEn,
  descriptionIt,
  descriptionEn,
  image,
  keywordsIt,
  keywordsEn,
  type = "website",
}) => {
  const { lang } = useLang();
  const { pathname } = useLocation();

  const origin =
    typeof window !== "undefined" && window.location
      ? window.location.origin
      : "";
  const canonical = `${origin}${pathname}`;

  const itTitle = titleIt || DEFAULTS.it.title;
  const enTitle = titleEn || DEFAULTS.en.title;
  const itDesc = descriptionIt || DEFAULTS.it.description;
  const enDesc = descriptionEn || DEFAULTS.en.description;
  const itKw = keywordsIt || DEFAULTS.it.keywords;
  const enKw = keywordsEn || DEFAULTS.en.keywords;

  const title = lang === "en" ? enTitle : itTitle;
  const description = lang === "en" ? enDesc : itDesc;
  const keywords = lang === "en" ? enKw : itKw;
  const ogLocale = lang === "en" ? "en_GB" : "it_IT";
  const ogLocaleAlt = lang === "en" ? "it_IT" : "en_GB";
  const previewImage = image || DEFAULT_IMAGE;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{`${title} · ${SITE_NAME}`}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonical} />

      {/* hreflang alternates — same URL, language switched via UI */}
      <link rel="alternate" hrefLang="it" href={canonical} />
      <link rel="alternate" hrefLang="en" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* OpenGraph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={previewImage} />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={ogLocaleAlt} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={previewImage} />
    </Helmet>
  );
};

export default SEO;
